import { ComponentType } from '@angular/cdk/portal';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { AccessRestrictedDialogComponent } from '../access';
import { PartnerService } from '../core/partner.service';
import { VConfigService } from '../vconfig/vconfig.service';
import { ActionList } from './action-list';
import { ActionListsDetailsStoreService } from './action-lists-details.store.service';
import { Action, ActionListDialog, ActionListDialogData, DeleteListDialog } from './actions';
import { ActionListsHistoryStoreService } from './history/action-lists-history.store.service';
import { BulkActionsProgressReporter } from './history/bulk-actions-reporter.service';

const ACTION_LIST_DIALOG_WIDTH = '660px';

@Component({
  templateUrl: './action-lists-details.component.html',
  styleUrls: ['action-lists-details.component.scss'],
  standalone: false,
})
export class ActionListsDetailsComponent implements OnInit, OnDestroy {
  actionListId: string;
  selectedTabIndex = 0;
  availableActions$: Observable<Action[]>;
  actionList$: Observable<ActionList>;
  actionsDisabled$: Observable<boolean>;
  subscriptions: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private actionListsDetailsStoreService: ActionListsDetailsStoreService,
    private actionListsHistoryStoreService: ActionListsHistoryStoreService,
    private dialog: MatDialog,
    private vconfigService: VConfigService,
    private partnerService: PartnerService,
    private readonly progress: BulkActionsProgressReporter,
  ) {}

  ngOnInit(): void {
    this.actionListId = this.route.snapshot.params.actionListId;
    this.progress.bindToActionList(this.actionListId);
    this.actionsDisabled$ = this.actionListsDetailsStoreService.actionDisabled$;
    this.actionList$ = this.actionListsDetailsStoreService.actionList$;
    this.availableActions$ = this.actionListsDetailsStoreService.availableActions$;
    this.actionListsDetailsStoreService.initializeStore(this.actionListId);
    this.setActiveTab(this.route.snapshot.params.tabId);
  }

  private setActiveTab(tabId: string): void {
    this.selectedTabIndex = this.actionListsDetailsStoreService.getTabIndex(tabId);
    this.handleTabChange();
  }

  showActionOrCTA(
    dialog: ComponentType<ActionListDialog>,
    data: ActionListDialogData,
    actionList: ActionList,
    featureId: string,
    locked: boolean,
    useBulkActionsMicroservice = false,
  ): void {
    if (locked) {
      this.showAccessRestrictedDialog(featureId);
      return;
    }
    this.showActionDialog(dialog, data, actionList, useBulkActionsMicroservice);
  }

  private showActionDialog(
    dialog: ComponentType<ActionListDialog>,
    data: ActionListDialogData,
    actionList: ActionList,
    useBulkActionsMicroservice: boolean,
  ): void {
    const dialogRef = this.dialog.open(dialog, {
      width: ACTION_LIST_DIALOG_WIDTH,
      data: {
        ...data,
        useBulkActionsMicroservice: useBulkActionsMicroservice,
      },
    });
    dialogRef.componentInstance.actionList = actionList;
    dialogRef.afterClosed().subscribe((success) => {
      if (success) {
        this.setActiveTab('history');
        this.actionListsHistoryStoreService.loadHistory(actionList.id);
      }
    });
  }

  private showAccessRestrictedDialog(featureId: string): void {
    this.dialog.closeAll();
    this.dialog.open(AccessRestrictedDialogComponent, {
      width: ACTION_LIST_DIALOG_WIDTH,
      data: { featureId: featureId },
    });
  }

  handleTabChange(): void {
    this.actionListsDetailsStoreService.updateTabRoute(this.selectedTabIndex);
  }

  showDeleteDialog(actionList: ActionList): void {
    const deleteDialogRef = this.dialog.open(DeleteListDialog, {
      width: ACTION_LIST_DIALOG_WIDTH,
    });
    deleteDialogRef.componentInstance.actionList = actionList;
    this.subscriptions.push(
      deleteDialogRef.afterClosed().subscribe((deleteFlag) => {
        if (deleteFlag) {
          this.actionListsDetailsStoreService.deleteActionList(actionList);
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.map((s) => s.unsubscribe());
  }
}
